import {
  Context,
  ContextContent,
  ContextContentBody,
  ContextContentHeader,
  ContextIcon,
  ContextTrigger,
} from "@/components/ai-elements/context";
import { ChatDebugSheet } from "@/components/chat/chat-debug-sheet";
import { toSegmentRows } from "@/components/chat/chat-debug/context-segments";
import { SegmentDot } from "@/components/chat/chat-debug/debug-tab-parts";
import {
  compactNumber,
  contextRingTokens,
} from "@/components/chat/chat-debug/format";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type {
  ContextWindowUsage,
  ConversationMessageMetadata,
  LLMModels,
} from "@shaarp/common";
import type { UIMessage } from "ai";
import { useMemo, useState } from "react";

/**
 * What the composer needs to hand the debug panel: the window of the turn the
 * user is looking at, plus the conversation it belongs to and the messages the
 * browser is holding (the only place chat errors exist).
 */
export type ChatContextUsageInfo = {
  usage: ContextWindowUsage;
  conversationId: string;
  messages: UIMessage<ConversationMessageMetadata>[];
  llmId?: LLMModels;
};

type ChatContextUsageProps = {
  info: ChatContextUsageInfo;
  className?: string;
};

/**
 * The context window of the last answered turn, as a ring in the composer.
 *
 * Hover gives the shape of the prompt, clicking opens the full debug panel.
 *
 * The warning background is this codebase's marker for an admin-only control —
 * the same one the composer's own admin action uses (`chat-text-input`, "add
 * company context") and the admin-mode switches in the user menu. It stays on
 * this control alone: the panel it opens is already titled as an admin surface,
 * and tinting that too would read as a warning about its contents.
 */
export function ChatContextUsage({ info, className }: ChatContextUsageProps) {
  const { t } = useTranslation();
  const { usage, conversationId, messages, llmId } = info;
  const [debugOpen, setDebugOpen] = useState(false);

  const rows = useMemo(() => toSegmentRows(usage.segments), [usage.segments]);

  return (
    <>
      <Context
        maxTokens={usage.contextWindowSize}
        usedTokens={contextRingTokens(usage)}
        // No `modelId`: the component would price the turn through tokenlens,
        // which knows two of this app's eight gateway model ids and returns
        // zero for the rest. A wrong cost is worse than none.
      >
        {/*
          The ring alone: the composer is where the user types, and the counter
          was eating room it needs more. The numbers are not lost — the exact
          count and the share are the title of the hover panel below, one step
          away instead of always on screen.
        */}
        <ContextTrigger>
          <Button
            aria-label={t("chat.debug.ring.ariaLabel")}
            className={cn(
              "size-8 bg-warning-foreground p-0 hover:bg-warning-foreground/80 active:bg-warning-foreground/80",
              className
            )}
            onClick={() => setDebugOpen(true)}
            size="sm"
            type="button"
            variant="ghost"
          >
            <ContextIcon />
          </Button>
        </ContextTrigger>
        <ContextContent align="end" side="top">
          <ContextContentHeader />
          <ContextContentBody className="space-y-1.5">
            {rows.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                {t("chat.debug.ring.noBreakdown")}
              </p>
            ) : (
              rows.map((row) => (
                <div
                  className="flex items-center justify-between gap-3 text-xs"
                  key={row.key}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <SegmentDot color={row.color} />
                    <span className="truncate text-muted-foreground">
                      {t(row.shortLabelKey)}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono">
                    {compactNumber(row.tokens)}
                  </span>
                </div>
              ))
            )}
            <p className="pt-1 text-[11px] text-muted-foreground">
              {t("chat.debug.ring.clickHint")}
            </p>
          </ContextContentBody>
        </ContextContent>
      </Context>

      <ChatDebugSheet
        conversationId={conversationId}
        llmId={llmId}
        messages={messages}
        onOpenChange={setDebugOpen}
        open={debugOpen}
        usage={usage}
      />
    </>
  );
}
